import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  ToastContext,
  type ToastApi,
  type ToastDismissReason,
  type ToastOptions,
  type ToastRecord,
} from './toast-context';
import { ToastViewport } from './ToastViewport';

/** A fourth toast evicts the oldest — three is already a lot of screen on a phone. */
export const MAX_TOASTS = 3;
const DEFAULT_DURATION_MS = 5_000;
const ERROR_DURATION_MS = 8_000;

/**
 * Owns the toast list. The context value is only `{ show, dismiss }` — both
 * stable — and the list itself goes to the viewport as props, so a feature
 * calling `useToast()` never re-renders because a toast came or went.
 */
export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const counter = useRef(0);

  const dismissWith = useCallback((id: string, reason: ToastDismissReason): void => {
    setToasts((prev) => {
      const target = prev.find((toast) => toast.id === id);
      if (target === undefined) return prev;
      target.onDismiss?.(reason);
      return prev.filter((toast) => toast.id !== id);
    });
  }, []);

  const show = useCallback((message: string, options: ToastOptions = {}): string => {
    counter.current += 1;
    const tone = options.tone ?? 'info';
    const record: ToastRecord = {
      id: `toast-${counter.current}`,
      message,
      tone,
      action: options.action,
      duration:
        options.duration === undefined
          ? tone === 'error'
            ? ERROR_DURATION_MS
            : DEFAULT_DURATION_MS
          : options.duration,
      key: options.key,
      onDismiss: options.onDismiss,
    };
    setToasts((prev) => {
      const replaced =
        record.key === undefined ? prev : prev.filter((toast) => toast.key !== record.key);
      const next = [...replaced, record];
      const overflow = next.length - MAX_TOASTS;
      if (overflow > 0) {
        for (const evicted of next.slice(0, overflow)) evicted.onDismiss?.('programmatic');
        return next.slice(overflow);
      }
      return next;
    });
    return record.id;
  }, []);

  const dismiss = useCallback(
    (id: string): void => {
      dismissWith(id, 'programmatic');
    },
    [dismissWith],
  );

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissWith} />
    </ToastContext.Provider>
  );
}
